import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../homepage/components/Header";
import Footer from "../homepage/components/Footer";
import Seo from "../../components/Seo";
import VisualDevFilters from "./components/VisualDevFilters";
import VisualDevEmptyState from "./components/VisualDevEmptyState";
import VisualDevGrid from "./components/VisualDevGrid";
import VisualDevCaseStudyModal from "./components/VisualDevCaseStudyModal";
import manifest from "../../data/visualDevelopmentManifest.json";

const visualDevelopmentSchema = {
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  name: "Visual Development & Art Direction — Skyline VFX",
  description:
    "Campaign key visuals, product concepts and cinematic brand imagery.",
};

const DEFAULT_CATEGORY = "campaign-key-visuals";

const VisualDevelopmentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const visibleCategories = useMemo(
    () => (manifest.categories ?? []).filter((c) => c.visible !== false),
    []
  );

  const allowedCategoryIds = useMemo(
    () => new Set(visibleCategories.map((c) => c.id)),
    [visibleCategories]
  );

  const categoryFromUrl = useMemo(() => {
    const value = new URLSearchParams(location.search).get("category");
    return allowedCategoryIds.has(value) ? value : null;
  }, [location.search, allowedCategoryIds]);

  const [selectedCategory, setSelectedCategory] = useState(
    categoryFromUrl ?? DEFAULT_CATEGORY
  );
  const [caseStudyProject, setCaseStudyProject] = useState(null);
  const [caseStudySlideIndex, setCaseStudySlideIndex] = useState(0);

  useEffect(() => {
    if (categoryFromUrl && categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFromUrl]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    navigate(
      {
        pathname: "/visual-development",
        search: `?category=${encodeURIComponent(categoryId)}`,
      },
      { replace: true }
    );
  };

  const projectsInCategory = useMemo(() => {
    return (manifest.projects ?? []).filter((p) => p.categoryId === selectedCategory);
  }, [selectedCategory]);

  const filterCategories = useMemo(
    () => visibleCategories.map((c) => ({ id: c.id, label: c.label })),
    [visibleCategories]
  );

  const openCaseStudy = (project) => {
    const slides = project?.slides;
    if (Array.isArray(slides) && slides.length > 0) {
      setCaseStudyProject(project);
      setCaseStudySlideIndex(0);
    }
  };

  const closeCaseStudy = () => {
    setCaseStudyProject(null);
    setCaseStudySlideIndex(0);
  };

  return (
    <>
      <Seo
        title="Visual Development & Art Direction - Skyline VFX"
        description="Visual development, campaign key visuals, product concepts and cinematic brand imagery by Skyline VFX."
        schema={visualDevelopmentSchema}
      />
      <div className="min-h-screen text-white bg-black">
        <Header />
        <main className="pt-8">
          <section className="pb-8 md:pb-10 border-b border-white/[0.06]">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-3 md:pt-5 lg:pt-6 text-center">
              <h1 className="text-[1.65rem] sm:text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-white leading-[1.15] tracking-tight">
                Visual Development &amp; Art Direction
              </h1>
              <p className="mt-6 md:mt-8 text-sm md:text-[0.9375rem] lg:text-base text-gray-500 font-light leading-normal max-w-none mx-auto md:whitespace-nowrap">
                Campaign key visuals, product concepts and cinematic brand imagery
              </p>
            </div>
            <VisualDevFilters
              categories={filterCategories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
          </section>

          <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-4 md:pt-6 pb-12 md:pb-16" aria-live="polite">
            {projectsInCategory.length === 0 ? (
              <VisualDevEmptyState categoryId={selectedCategory} />
            ) : (
              <VisualDevGrid projects={projectsInCategory} onProjectActivate={openCaseStudy} />
            )}
          </section>
        </main>
        <Footer />
        {caseStudyProject ? (
          <VisualDevCaseStudyModal
            project={caseStudyProject}
            slideIndex={caseStudySlideIndex}
            onSlideIndexChange={setCaseStudySlideIndex}
            onClose={closeCaseStudy}
          />
        ) : null}
      </div>
    </>
  );
};

export default VisualDevelopmentPage;
